// The typed tip registry behind <HelpTip id="..." />: the small "?" next to a
// label. Each tip says what the thing is in 1-3 plain sentences and links to the
// article (and optionally a `##` section of it) that shows how to use it.
//
// Ids are `<audience>.<area>.<thing>`. Add a tip here, then place it with
// `<HelpTip id="admin.rooms.create" />`; the ids are a closed type, so a typo
// fails the typecheck, and the tests check every article/anchor exists and every
// placed id is registered.

export interface HelpTipDef {
  /** A few words naming the thing; also the "?" button's accessible name. */
  title: string;
  /** 1-3 plain sentences: what it is, and the one thing to know to use it. */
  text: string;
  /** The article id this tip leads to: `<audience>/<slug>`. */
  article: string;
  /** A `##` heading anchor inside that article. */
  anchor?: string;
}

export const TIPS = {
  // Admin
  "admin.rooms.rooms": {
    title: "Rooms",
    text: "A room is one deal. Create one for each company you are selling, then add documents and invite the seller and buyers.",
    article: "admin/get-started",
    anchor: "steps",
  },
  "admin.rooms.create": {
    title: "Create a room",
    text: "Name the room after the deal and pick the fiscal year end. The checklist builds its yearly and quarterly requests from it.",
    article: "admin/create-a-room",
    anchor: "steps",
  },
  "admin.rooms.active": {
    title: "Active rooms",
    text: "Each row shows how much of the checklist is covered and what needs you: documents to review and buyer questions.",
    article: "admin/read-the-rooms-list",
    anchor: "what-each-room-shows",
  },
  "admin.rooms.admin-role": {
    title: "Admin role",
    text: "This is your part in the deal. Keep Intermediary / broker if you run the deal for the seller, and choose Buyer or Seller only if you are a principal acting without a broker.",
    article: "admin/create-a-room",
    anchor: "steps",
  },
  "admin.rooms.checklist-summary": {
    title: "Checklist at a glance",
    text: "These figures show how many requested items are resolved, covered by a document, signed as not applying by the seller, still missing, or waiting on review. Select one to open exactly those items.",
    article: "admin/read-the-rooms-list",
    anchor: "what-each-room-shows",
  },
  "admin.room.processing-indicator": {
    title: "Processing indicator",
    text: "This shows documents that have not finished filing, reading or redaction. It spins while a step runs and shows an alert when everything left is waiting on you.",
    article: "admin/watch-processing",
    anchor: "what-you-see",
  },
  "admin.room.qa-badge": {
    title: "Questions waiting on you",
    text: "The number on the Q&A tab counts buyer questions that are new, or that have a seller draft waiting for you to release. It goes away once you have dealt with them.",
    article: "admin/answer-buyer-questions",
    anchor: "find-questions",
  },
  "admin.data.add-documents": {
    title: "Add documents",
    text: "Drop in files or a whole folder, in any amount, and ClosingVault reads, names, files, dates and redacts each one. You do not need to sort anything first.",
    article: "admin/upload-documents",
    anchor: "steps",
  },
  "admin.data.ask": {
    title: "Ask the Data Room",
    text: "Type a plain request such as \"Show me all of the P&Ls\" and ClosingVault lists the matching documents. It finds documents. It does not give opinions about them.",
    article: "admin/ask-the-data-room",
    anchor: "steps",
  },
  "admin.data.needs-a-look": {
    title: "Needs a look",
    text: "These are documents the AI filed with low confidence or could not file. Choose Review one by one to accept its best guess in a click.",
    article: "admin/review-uncertain-filing",
    anchor: "before-you-start",
  },
  "admin.data.unreadable": {
    title: "Couldn't read",
    text: "These files are damaged, protected or too poor to read, so the seller has been asked for a readable copy. You can open the original, upload a replacement, or clear the flag if the file is fine.",
    article: "admin/unreadable-files",
    anchor: "options-on-each-file",
  },
  "admin.data.filter-menu": {
    title: "Filter by accounting basis",
    text: "Use this to show only financial statements prepared on one basis, such as accrual or cash. ClosingVault reads the basis from each statement, and you can correct it in the review panel.",
    article: "admin/find-documents",
    anchor: "search-and-filter",
  },
  "admin.data.new-folder": {
    title: "New folder",
    text: "Add a folder for material the checklist does not cover, such as the teaser or the CIM. Top-level folders let you choose who gets them by default.",
    article: "admin/folders-and-visibility",
    anchor: "add-a-folder",
  },
  "admin.data.change-audience": {
    title: "Folder audience",
    text: "This decides who gets the selected folder by default: every invited buyer, buyers who signed the NDA, or only people you give access to. Making a folder reach buyers earlier shows a warning first.",
    article: "admin/folders-and-visibility",
    anchor: "who-gets-a-folder",
  },
  "admin.data.version-suggestions": {
    title: "Possible new versions",
    text: "These uploads look like newer versions of documents already in the room, but not clearly enough to merge on their own. Merging makes the new file current and keeps the old one in version history.",
    article: "admin/duplicates-and-new-versions",
    anchor: "possible-new-versions",
  },
  "admin.upload.skipped": {
    title: "Skipped duplicates",
    text: "A file that is byte-for-byte identical to one already in the room is not added again, and you are told. Open the list to see which document each one matched.",
    article: "admin/duplicates-and-new-versions",
    anchor: "exact-duplicates-are-skipped",
  },
  "admin.manage-document.period": {
    title: "Period and date of data",
    text: "The period is how often the document repeats, such as annual or quarterly. The date of data is the last day its figures cover, and you can confirm the AI's suggestion in one click.",
    article: "admin/change-folder-or-date",
    anchor: "steps",
  },
  "admin.review.queue": {
    title: "Review queue",
    text: "This counts documents still waiting to be filed. Choose the best suggestion for each, and the next document opens by itself.",
    article: "admin/review-uncertain-filing",
    anchor: "steps",
  },
  "admin.review.accounting-basis": {
    title: "Accounting basis",
    text: "ClosingVault reads whether a statement is on an accrual, cash or modified cash basis. If it read it wrongly, correct it here. The correction is recorded as yours.",
    article: "admin/review-uncertain-filing",
    anchor: "the-review-classification-dialog",
  },
  "admin.processing.needs-attention": {
    title: "Needs your attention",
    text: "This filter shows only documents waiting on a person: a filing to review, a redaction to review, or a scan that could not be read. Each row has the action that unblocks it.",
    article: "admin/watch-processing",
    anchor: "steps-for-documents-waiting-on-you",
  },
  "admin.redaction.legend": {
    title: "Redaction key",
    text: "A black box will be hidden, a striped box needs your decision, and a dashed box is kept visible. Choose Hide or Keep visible for each striped mark.",
    article: "admin/review-redaction",
    anchor: "read-the-page",
  },
  "admin.redaction.view-switch": {
    title: "Original or redacted copy",
    text: "Original with marks is where you review. Redacted copy shows the copy that everyone who opens the document sees.",
    article: "admin/review-redaction",
    anchor: "read-the-page",
  },
  "admin.redaction.approve": {
    title: "Approve the redaction",
    text: "This records that you checked the redaction. Any mark still undecided is hidden, the redacted copy is made and checked, and the next document opens.",
    article: "admin/review-redaction",
    anchor: "steps",
  },
  "admin.checklist.release": {
    title: "Release to seller",
    text: "Each row is a phase: its status, start and due dates, and progress. The seller sees nothing in a phase until you release it, or until its start date arrives. Type a date in place to change it.",
    article: "admin/release-a-phase",
    anchor: "release-a-phase-by-hand",
  },
  "admin.checklist.item-status": {
    title: "Item status",
    text: "Missing means no document yet. Waiting on review means the AI matched one and the match needs a look. Covered means a document answers it, and Doesn't apply is the seller's own statement.",
    article: "admin/read-the-checklist",
    anchor: "item-statuses",
  },
  "admin.checklist.two-ticks": {
    title: "Classified and Certified",
    text: "Classified means the AI has filed at least one document into this section. Certified means the seller confirmed the section is complete, and only the seller can do that.",
    article: "admin/read-the-checklist",
    anchor: "phases-and-categories",
  },
  "admin.checklist-source.title": {
    title: "Where the checklist comes from",
    text: "Choose the ClosingVault checklist, the standard one, your own spreadsheet, a saved checklist, or skip. Choosing a source is a one-time start, and you can add, edit and remove items afterwards.",
    article: "admin/choose-a-checklist",
    anchor: "steps",
  },
  "admin.checklist-source.save-for-reuse": {
    title: "Save for reuse",
    text: "Tick this to keep an uploaded checklist under a name so you can start future rooms from it. Saving checklists is included with the Professional plan.",
    article: "admin/saved-checklists",
    anchor: "save-a-checklist",
  },
  "admin.checklists.title": {
    title: "Saved checklists",
    text: "These are your firm's own reusable checklists. Deleting one never changes rooms already set up from it.",
    article: "admin/saved-checklists",
    anchor: "before-you-start",
  },
  "admin.access.owners": {
    title: "Owners",
    text: "Owners are the business owners of the company being sold. Together they decide LOIs and exclusivity, and a decision needs approval from owners holding more than 50% of the company.",
    article: "admin/invite-owners",
    anchor: "steps",
  },
  "admin.access.ownership-total": {
    title: "Ownership total",
    text: "The percentages cannot add up to more than 100%. If owners together hold 50% or less, nothing can be approved and a warning shows on every page of the room.",
    article: "admin/invite-owners",
    anchor: "change-percentages-or-remove-an-owner",
  },
  "admin.access.invite-role": {
    title: "Role in this deal",
    text: "The role sets the person's starting access. A buyer's diligence team starts at the teaser only, and your own team gets broad access.",
    article: "admin/invite-a-buyer",
    anchor: "steps",
  },
  "admin.access.end-picker": {
    title: "When access ends",
    text: "Buyer access always ends on a date you choose. It defaults to two weeks and can run at most two months from today.",
    article: "admin/invite-a-buyer",
    anchor: "the-access-end-date",
  },
  "admin.access.team-roles": {
    title: "Team roles",
    text: "Roles such as Financial or Legal are labels for who does what on the deal. Today only Legal has an effect: a seller's-team member tagged Legal can read the letters of intent.",
    article: "admin/invite-seller-team",
    anchor: "what-roles-do",
  },
  "admin.access.unlock-stage": {
    title: "Unlocks when the buyer reaches",
    text: "Gate a grant to a stage so the buyer only gets the folders once they reach it. Leave it on Visible now to give access immediately.",
    article: "admin/manage-access-rights",
    anchor: "give-someone-specific-access",
  },
  "admin.access.manage-rights": {
    title: "Manage access rights",
    text: "Use this for someone already in the room, to choose exactly which folders they see, what they can do, and how long it lasts. To bring in a new person, use Invite to room.",
    article: "admin/manage-access-rights",
    anchor: "give-someone-specific-access",
  },
  "admin.buyers.pipeline": {
    title: "Buyer pipeline",
    text: "Every buyer is shown on a seven-step bar from Teaser to Close, with a one-line state read from recorded facts. It is an order by progress, not a score.",
    article: "admin/follow-buyer-progress",
    anchor: "buyer-pipeline",
  },
  "admin.buyers.cim-access": {
    title: "CIM and document access",
    text: "Access follows the funnel. Signing the NDA unlocks the CIM, and from the LOI on, due diligence opens the folders you grant each buyer. Expires shows when their access ends.",
    article: "admin/follow-buyer-progress",
    anchor: "cim-and-document-access",
  },
  "admin.buyers.funnel-override": {
    title: "Move without a signature",
    text: "A buyer reaches NDA signed and LOI submitted by signing. If you must move them without a signature, an override needs a reason and is written to the activity log.",
    article: "admin/manage-a-buyer",
    anchor: "move-a-buyer-between-stages",
  },
  "admin.buyers.team-requests": {
    title: "Team requests",
    text: "A buyer asked to add these people to their team. When you approve, you choose how long each person's access lasts, and a team member sees what that buyer sees.",
    article: "admin/manage-buyer-team",
    anchor: "approve-a-request-from-the-buyer",
  },
  "admin.loi.pending-vote": {
    title: "Pending the owners' vote",
    text: "What you enter on an LOI is a proposal. It takes effect when owners holding more than 50% of the company approve it.",
    article: "admin/owners-vote",
    anchor: "what-you-see-while-a-vote-is-open",
  },
  "admin.loi.respond-toggle": {
    title: "Let the owners respond",
    text: "When on, the owners can start an accept, counter or reject themselves. You can always propose too, and the owners always vote.",
    article: "admin/review-lois",
    anchor: "loi-settings",
  },
  "admin.loi.exclusivity-period": {
    title: "Exclusivity period",
    text: "While exclusivity is in effect, every other buyer's access is paused, not removed, and it returns on its own when the period ends. Pick 30, 60, 90 or 120 days, or a custom length.",
    article: "admin/accept-an-loi",
    anchor: "steps",
  },
  "admin.loi.clock-toggles": {
    title: "Who sees the countdown",
    text: "Choose whether the buyer holding exclusivity, and the paused buyers, can see the clock. When it is off, paused buyers are told access is paused, without a date.",
    article: "admin/extend-or-end-exclusivity",
    anchor: "choose-who-sees-the-countdown",
  },
  "admin.agreements.master-nda": {
    title: "Master NDA",
    text: "Set the room's NDA once and issuing an agreement uses it by default. Choose a different document for a buyer only if you negotiated a redline with them.",
    article: "admin/issue-the-nda",
    anchor: "set-the-master-nda",
  },
  "admin.qa.buyer-questions": {
    title: "Buyer Questions",
    text: "Answers you share here go to every buyer who can see the CIM, in your wording and without the name of the buyer who asked. Buyers who reach the CIM later see everything.",
    article: "admin/share-with-all-buyers",
    anchor: "who-sees-it",
  },
  "admin.risks.visibility": {
    title: "Who can see it",
    text: "This sets who can read the entry in the app and in the weekly digest: the deal team only, the deal team and seller, or everyone including buyers. Buyers see an owner as a role, never a name.",
    article: "admin/track-risks",
    anchor: "add-an-entry",
  },
  "admin.contracts.flag": {
    title: "Contract flags",
    text: "Flags compare the dates each document states with today and the expected closing date. They are plain date facts, such as Expired or Within 90 days.",
    article: "admin/contracts-register",
    anchor: "what-the-register-shows",
  },
  "admin.activity.show-all": {
    title: "Views and system activity",
    text: "By default the log shows what people did. Tick this to include document views and automatic steps ClosingVault took itself.",
    article: "admin/read-activity-log",
    anchor: "what-is-in-it",
  },
  "admin.settings.digest": {
    title: "Weekly digest",
    text: "A one-page email each week with checklist progress, changes, the timeline and open risks. Each person gets only what they can already see in the room.",
    article: "admin/set-up-weekly-digest",
    anchor: "steps",
  },
  "admin.settings.alerts": {
    title: "Buyer activity alerts",
    text: "An email to you only, listing who opened which document, how many pages and for how long. It is off until you turn it on, and you pick how often it can send.",
    article: "admin/buyer-activity-alerts",
    anchor: "steps",
  },
  "admin.settings.status": {
    title: "Room status",
    text: "Archiving ends the deal for the other side and leaves you a read-only view. Deleting moves the room to Trash for 30 days, and restoring it carries a recovery fee.",
    article: "admin/archive-restore-or-delete-a-room",
    anchor: "archive-a-room",
  },
  "admin.settings.recovery-contact": {
    title: "Recovery contact",
    text: "This is an address outside your owners' mailboxes. It is emailed about every owner change, and recovering your account would start there if your firm lost access to every owner. It cannot sign in or see any data.",
    article: "admin/firm-settings",
    anchor: "recovery-contact",
  },
  "admin.team.title": {
    title: "Owners and admins",
    text: "Owners can see and take over every room and manage billing. Rooms Admins run the rooms they are given and use your plan's admin seats.",
    article: "admin/manage-admins-and-owners",
    anchor: "roles",
  },
  "admin.billing.usage": {
    title: "Plan and usage",
    text: "Each meter shows how much of a plan limit you have used. A meter turns red at its limit, and ClosingVault then offers a way to make room.",
    article: "admin/plan-and-usage",
    anchor: "what-you-see",
  },
  "admin.billing.ask-limit": {
    title: "Ask the Data Room allowance",
    text: "This is how many Ask the Data Room questions your plan allows each day. It can be a set number, or Unlimited.",
    article: "admin/ask-the-data-room",
    anchor: "common-questions",
  },
  "admin.trial.banner": {
    title: "Free trial",
    text: "The trial lasts 30 days with one deal room and every feature, and it includes the standard checklist. Upgrade at any time to keep going.",
    article: "admin/plan-and-usage",
    anchor: "free-trial",
  },

  // Seller
  "seller.rooms.list": {
    title: "Your data rooms",
    text: "Each deal you are the seller in is a room. Open one to add documents and see what the buyer side has asked for.",
    article: "seller/getting-into-your-room",
    anchor: "open-your-room",
  },
  "seller.dashboard.upload": {
    title: "Add documents",
    text: "Drop files or a whole folder here, scans included. ClosingVault reads, names, dates and files each one for you, so there is nothing to sort or rename first.",
    article: "seller/upload-documents",
    anchor: "steps",
  },
  "seller.dashboard.progress": {
    title: "Progress",
    text: "This shows how much of the checklist is done, counted as covered, missing, waiting on review or not applicable. Items tick off on their own as your documents are filed, and clicking a phase opens its missing items.",
    article: "seller/checklist-overview",
    anchor: "shortcuts-from-the-dashboard",
  },
  "seller.dashboard.upload-summary": {
    title: "Upload summary",
    text: "After a batch finishes, this line counts what was added, what was skipped as an exact copy of a file already in the room, and what failed. Click the links under it to see the names.",
    article: "seller/duplicates-and-new-versions",
    anchor: "identical-files-are-skipped",
  },
  "seller.dashboard.processing-estimate": {
    title: "Processing time",
    text: "This is an estimate of how long ClosingVault needs to file, read and redact what you just added. You can keep uploading while it works.",
    article: "seller/check-processing-status",
    anchor: "steps",
  },
  "seller.dashboard.room-guide": {
    title: "Your room guide",
    text: "A short checklist for your first visit. Each step ticks itself off as you do it, and you can hide the guide and bring it back later.",
    article: "seller/tour-of-your-room",
    anchor: "the-room-guide",
  },
  "seller.dashboard.phase-dates": {
    title: "Phase dates",
    text: "Your intermediary sets when a phase starts and when it is due. Items without their own date use the phase due date.",
    article: "seller/phases-and-due-dates",
    anchor: "phases-are-released-by-your-intermediary",
  },
  "seller.dashboard.due-soon": {
    title: "Due soon",
    text: "Items that are still missing and either overdue or due within 7 days, earliest first. Once an item is covered, waiting on review or marked not applicable, it leaves this list.",
    article: "seller/phases-and-due-dates",
    anchor: "due-soon",
  },
  "seller.dashboard.waiting-on-you": {
    title: "Waiting on you",
    text: "What needs a person on your side right now: buyer questions to answer, documents to file, and, for owners, votes. Click a line to go straight to it.",
    article: "seller/answer-buyer-questions",
    anchor: "find-your-questions",
  },
  "seller.dashboard.exclusivity-card": {
    title: "Exclusivity",
    text: "Shown to owners when one buyer holds exclusivity, or when an accepted letter of intent is waiting for the owners' vote. While it runs, every other buyer's access is paused.",
    article: "seller/exclusivity",
    anchor: "where-to-see-it",
  },
  "seller.dashboard.unreadable-card": {
    title: "Files we could not read",
    text: "These files are locked, damaged or blank, so they cannot be filed or counted yet. Send a readable copy and ClosingVault files it for you.",
    article: "seller/fix-unreadable-files",
    anchor: "how-you-will-know",
  },
  "seller.dashboard.does-not-apply": {
    title: "Doesn't apply to my company",
    text: "Use this when the item is not present in, or never used by, your company. You confirm a short statement, and the item then counts as resolved without a document.",
    article: "seller/mark-item-not-applicable",
    anchor: "steps",
  },
  "seller.app.new-version-bar": {
    title: "New version available",
    text: "A newer version of ClosingVault is ready. Click Reload when you are not in the middle of an upload; it never reloads on its own.",
    article: "seller/tour-of-your-room",
    anchor: "when-a-new-version-of-closingvault-is-available",
  },
  "seller.documents.folders": {
    title: "Folders",
    text: "ClosingVault files each document into a folder for you. Click a folder to list only its documents, or All documents to see everything.",
    article: "seller/find-a-document",
    anchor: "browse-the-folders",
  },
  "seller.documents.ask": {
    title: "Ask the Data Room",
    text: "Ask in plain words, such as \"Show me all of the P&Ls\", and it lists the matching documents. It finds documents; it does not give opinions about them.",
    article: "seller/find-a-document",
    anchor: "ask-the-data-room",
  },
  "seller.documents.filter-menu": {
    title: "Filter by accounting basis",
    text: "Narrow the list to financial statements prepared on one basis, such as accrual or cash. The menu appears only when some statements have a basis.",
    article: "seller/find-a-document",
    anchor: "narrow-the-list",
  },
  "seller.documents.unreadable-chip": {
    title: "Couldn't read",
    text: "Shows only the files ClosingVault could not read. Each one needs a new copy from you before it can be filed or counted.",
    article: "seller/fix-unreadable-files",
    anchor: "how-you-will-know",
  },
  "seller.documents.manage-link": {
    title: "Change folder or date",
    text: "If ClosingVault filed or dated a document wrongly, fix it here. Your change is saved under your name.",
    article: "seller/move-or-redate-a-document",
    anchor: "open-manage-document",
  },
  "seller.documents.possible-versions": {
    title: "Possible new versions",
    text: "These uploads look like newer copies of documents already in the room. Review them to make the new file the current version, or keep both.",
    article: "seller/duplicates-and-new-versions",
    anchor: "files-that-look-like-a-newer-version",
  },
  "seller.documents.merge-keep": {
    title: "Merge or keep separate",
    text: "Merge makes the new file the current version and keeps the old one in the version history. Keep separate leaves two documents and does not suggest that pair again.",
    article: "seller/duplicates-and-new-versions",
    anchor: "files-that-look-like-a-newer-version",
  },
  "seller.documents.redacted-caption": {
    title: "The copy buyers see",
    text: "ClosingVault hides account numbers and similar personal data in a copy. You and every buyer see this same redacted copy.",
    article: "seller/what-buyers-see",
    anchor: "one-redacted-copy-for-everyone",
  },
  "seller.processing.status-buttons": {
    title: "Processing status",
    text: "Each button filters the list by where documents stand and shows a count. Hover over one for a plain explanation.",
    article: "seller/check-processing-status",
    anchor: "the-status-buttons",
  },
  "seller.processing.progress-panel": {
    title: "Time left",
    text: "An estimate for each stage: Filing, Reading scans and Redaction. It updates about every 10 seconds and is never exact.",
    article: "seller/check-processing-status",
    anchor: "steps",
  },
  "seller.processing.redaction-indicator": {
    title: "Redaction status",
    text: "Redaction pending means the redacted copy is still being made. Buyers see Being prepared until it is complete.",
    article: "seller/what-happens-after-upload",
    anchor: "the-steps",
  },
  "seller.checklist.count-tiles": {
    title: "Checklist counts",
    text: "Resolved, Covered, You signed \"doesn't apply\" and Still missing are also filters. Click a tile to list only those items, and click it again to show everything.",
    article: "seller/checklist-overview",
    anchor: "steps",
  },
  "seller.checklist.optional-note": {
    title: "Optional items",
    text: "An optional item stays Missing until you give a document, or confirm that it is not present in or used by your company.",
    article: "seller/mark-item-not-applicable",
    anchor: "when-to-use-it",
  },
  "seller.checklist.item-status": {
    title: "Item status",
    text: "Covered means a document answers it. Waiting on review means ClosingVault matched one and the deal team confirms it. Missing means nothing yet.",
    article: "seller/checklist-overview",
    anchor: "read-an-item",
  },
  "seller.checklist.link-document": {
    title: "Link a document",
    text: "Pick documents already in the room to cover this item. Likely matches are listed first, and nothing is uploaded again.",
    article: "seller/cover-an-item",
    anchor: "link-a-document-that-is-already-in-the-room",
  },
  "seller.checklist.certify-controls": {
    title: "Certify a section",
    text: "Tell the deal team the whole section is complete, or that it does not apply to your business. It is a statement recorded with your name, so only certify what is true.",
    article: "seller/certify-a-section",
    anchor: "what-you-can-say",
  },
  "seller.checklist.phase-header": {
    title: "Phase dates",
    text: "The start and due dates your intermediary set for this phase. You only see a phase once it has been released to you.",
    article: "seller/phases-and-due-dates",
    anchor: "phases-are-released-by-your-intermediary",
  },
  "seller.checklist.phase-questions": {
    title: "Questions for this phase",
    text: "Short questions from your intermediary, separate from buyer questions. Type an answer and click Save answer, or fill in the list it asks for.",
    article: "seller/answer-phase-questions",
    anchor: "steps",
  },
  "seller.lois.status": {
    title: "LOI status",
    text: "The status records what happened to the signed letter of intent the buyer uploaded: Submitted, Accepted, Countered or Rejected. Accepted can wait for the owners' vote.",
    article: "seller/review-an-loi",
    anchor: "what-the-statuses-mean",
  },
  "seller.lois.response-actions": {
    title: "Accept, Counter, Reject",
    text: "Your response is your vote as an owner. It takes effect when owners holding more than half of the company approve it; every decision asks \"Are you sure?\" first.",
    article: "seller/respond-to-an-loi",
    anchor: "how-the-decision-is-made",
  },
  "seller.lois.intermediary-note": {
    title: "Your intermediary proposes",
    text: "In this room your intermediary enters the response. It is only a proposal until owners holding more than 50% of the company approve it, and you vote on it here.",
    article: "seller/respond-to-an-loi",
    anchor: "who-can-start-a-response",
  },
  "seller.lois.vote-panel": {
    title: "The owners' vote",
    text: "Each owner's vote counts for their share of the whole company. More than 50% approving carries it; 50% or more declining drops it; exactly half never approves.",
    article: "seller/vote-on-a-loi-proposal",
    anchor: "how-the-vote-works",
  },
  "seller.lois.countdown": {
    title: "Exclusivity countdown",
    text: "Time left until the exclusivity period ends at midnight in the room's time zone. Other buyers' access is paused until then and returns on its own.",
    article: "seller/exclusivity",
    anchor: "while-exclusivity-is-in-effect",
  },
  "seller.questions.status-chip": {
    title: "Question status",
    text: "Needs your answer means no draft yet. Waiting for release means your intermediary has your draft and has not sent it. Sent to the buyer means it is final.",
    article: "seller/answer-buyer-questions",
    anchor: "find-your-questions",
  },
  "seller.questions.suggest-documents": {
    title: "Suggest documents",
    text: "ClosingVault looks through the room for documents that might answer this question and lists them. It never writes the answer for you.",
    article: "seller/answer-buyer-questions",
    anchor: "steps",
  },
  "seller.contracts.flag": {
    title: "Contract flags",
    text: "A flag only compares a date with today or the expected closing date: Expired, Within 90 days or Before closing. It is not an opinion about the contract.",
    article: "seller/contracts-register",
    anchor: "read-the-list",
  },
  "seller.login.email-code": {
    title: "Email me a code",
    text: "No password needed. Enter your email address and we send a 6-digit code. Use it the first time you sign in, or if you forgot your password.",
    article: "seller/getting-into-your-room",
    anchor: "sign-in-for-the-first-time",
  },
  "seller.rooms.team-label": {
    title: "Seller's team",
    text: "You work in this room as part of the seller's team. You have the same access to documents and the checklist as the owners, but you do not vote on letters of intent.",
    article: "seller/your-seller-team",
    anchor: "what-both-can-do",
  },

  // Buyer
  "buyer.rooms.list": {
    title: "Your data rooms",
    text: "Every room you have been invited to is listed here. Click a room to open it. A room disappears from the list when your access to it ends.",
    article: "buyer/find-your-way-around",
    anchor: "your-data-rooms",
  },
  "buyer.room.progress": {
    title: "Your progress",
    text: "This shows where you are in the process, from the teaser to closing. More of the room opens up as you move through the steps.",
    article: "buyer/follow-your-progress",
    anchor: "the-steps",
  },
  "buyer.room.ask": {
    title: "Ask the Data Room",
    text: "Ask in plain words, such as \"show me the bank statements\". It points you to the matching documents, and never gives opinions or risk judgments.",
    article: "buyer/ask-the-data-room",
    anchor: "steps",
  },
  "buyer.login.email-code": {
    title: "Email me a code",
    text: "ClosingVault confirms your email with a 6-digit code. Use it the first time you come in, or any time you forget your password.",
    article: "buyer/sign-in-with-an-emailed-code",
    anchor: "steps",
  },
  "buyer.login.code": {
    title: "Your 6-digit code",
    text: "Type the code from the email we just sent, then click Continue. The first time, you then choose a password of at least 10 characters.",
    article: "buyer/sign-in-with-an-emailed-code",
    anchor: "common-questions",
  },
  "buyer.room.views": {
    title: "Documents, Questions and Buyer Questions",
    text: "Documents holds folders and the document list. Questions goes to the deal team. Buyer Questions appears once you can see the CIM.",
    article: "buyer/find-your-way-around",
    anchor: "inside-a-room",
  },
  "buyer.room.guide": {
    title: "Find your way around",
    text: "This short guide ticks off by itself as you browse, ask and read. Hide it any time, and bring it back with Show the room guide.",
    article: "buyer/find-your-way-around",
    anchor: "the-first-visit-guide",
  },
  "buyer.nda.agreements": {
    title: "Agreements to sign",
    text: "Read the agreement, type your name and click Sign to unlock the CIM. You cannot sign until the agreement has loaded.",
    article: "buyer/sign-the-nda",
    anchor: "steps",
  },
  "buyer.nda.sign": {
    title: "Sign",
    text: "Typing your name and clicking Sign is your electronic signature. If it stops halfway, the button changes to Finish signing.",
    article: "buyer/sign-the-nda",
    anchor: "common-questions",
  },
  "buyer.folders.tree": {
    title: "Folders",
    text: "ClosingVault files each document into a folder for you. You only see folders that hold documents you can open. Pick a folder to list its documents.",
    article: "buyer/browse-by-folder",
    anchor: "what-you-see",
  },
  "buyer.folders.clear": {
    title: "Results title",
    text: "The title says what the list shows: All documents, a Folder path, Search results, or an Answer. Click the X beside it to go back to all documents.",
    article: "buyer/browse-by-folder",
    anchor: "steps",
  },
  "buyer.search.box": {
    title: "Find a document",
    text: "Type words from a document name or its text. Every word has to match. Pick a folder first to search inside it.",
    article: "buyer/search-for-a-document",
    anchor: "how-the-search-works",
  },
  "buyer.ask.answer": {
    title: "How your question was read",
    text: "The line above the list says which folder, year and period your question was matched to. Click the X to return to all documents.",
    article: "buyer/ask-the-data-room",
    anchor: "reading-the-summary-line",
  },
  "buyer.documents.badges": {
    title: "View only, Download allowed, Being prepared",
    text: "View only means you read it here but cannot download it. Being prepared means the document is not ready to open yet.",
    article: "buyer/open-a-document",
    anchor: "the-badges",
  },
  "buyer.viewer.redaction": {
    title: "Blacked-out numbers",
    text: "Sensitive numbers such as SSNs and account numbers are masked. Everyone in the room sees the same redacted copy.",
    article: "buyer/understand-redacted-documents",
    anchor: "everyone-sees-the-same-copy",
  },
  "buyer.viewer.view-only": {
    title: "View only and Download",
    text: "View only documents cannot be downloaded or printed, and carry a watermark with your email. A Download button appears only where the deal team allows it.",
    article: "buyer/download-a-document",
    anchor: "what-you-get",
  },
  "buyer.documents.new-alerts": {
    title: "Email me about new documents",
    text: "Get at most one email a day when documents are added to what you can see. Clear the box to stop.",
    article: "buyer/get-new-document-emails",
    anchor: "what-the-email-is",
  },
  "buyer.exclusivity.paused": {
    title: "Data room access is paused",
    text: "Another buyer's LOI was tentatively accepted. Your access is not removed and comes back when the exclusivity period ends.",
    article: "buyer/when-your-access-is-paused",
    anchor: "what-you-see",
  },
  "buyer.exclusivity.clock": {
    title: "Exclusivity countdown",
    text: "Time left until the exclusivity period ends, in the room's time zone. The deal team chooses whether buyers see this clock.",
    article: "buyer/understand-exclusivity",
    anchor: "the-countdown-clock",
  },
  "buyer.questions.form": {
    title: "Your question",
    text: "Questions go privately to the deal team. Pick a document under About a document if your question is about one.",
    article: "buyer/ask-the-deal-team-a-question",
    anchor: "steps",
  },
  "buyer.questions.status": {
    title: "Open, Answered, Closed",
    text: "Open means you are waiting for an answer. Answered means the deal team replied. Linked documents in an answer open in the viewer.",
    article: "buyer/ask-the-deal-team-a-question",
    anchor: "read-the-answers",
  },
  "buyer.buyer-questions.tab": {
    title: "Buyer Questions",
    text: "Questions other buyers asked that the deal team answered and chose to share. Your name is never shown.",
    article: "buyer/read-buyer-questions",
    anchor: "when-it-appears",
  },
  "buyer.loi.card": {
    title: "Letter of intent",
    text: "Your LOI is a document you sign yourself and upload. The seller side then accepts, counters or rejects it here.",
    article: "buyer/submit-your-signed-loi",
    anchor: "before-you-start",
  },
  "buyer.loi.upload": {
    title: "Your signed letter of intent",
    text: "Choose your signed PDF, Word file or picture, up to 50 MB, then click Submit signed LOI.",
    article: "buyer/submit-your-signed-loi",
    anchor: "steps",
  },
  "buyer.loi.status": {
    title: "LOI status",
    text: "Submitted, Accepted, Countered or Rejected. After a counter or a rejection you can upload a revised signed LOI.",
    article: "buyer/track-your-loi",
    anchor: "the-statuses",
  },
  "buyer.team.page": {
    title: "My team",
    text: "Name the colleagues and advisers who work this deal with you. The intermediary approves each person. Only you and the intermediary see the list.",
    article: "buyer/add-people-to-your-team",
    anchor: "steps",
  },
  "buyer.team.roles": {
    title: "Roles",
    text: "Financial, Legal, Risk, HR, Technical and Operations are labels that show who does what. They are optional and you can change them later.",
    article: "buyer/edit-roles-or-remove-a-team-member",
    anchor: "edit-roles",
  },
  "buyer.team.exclusivity": {
    title: "Why your team matters",
    text: "If your LOI is accepted, your named team keeps access while other buyers are paused.",
    article: "buyer/add-people-to-your-team",
    anchor: "why-a-team-matters",
  },
  "buyer.access.end-date": {
    title: "Your access end date",
    text: "Buyer access always ends on a date the intermediary sets. Review what you need before then. The deal team can extend it.",
    article: "buyer/when-your-access-ends",
    anchor: "see-your-end-date",
  },
} as const satisfies Record<string, HelpTipDef>;

export type HelpTipId = keyof typeof TIPS;

/** A tip by id, or undefined for an id that is not registered. */
export function getTip(id: string): HelpTipDef | undefined {
  return (TIPS as Record<string, HelpTipDef>)[id];
}
